import { create } from 'zustand'

export type EngineStatus = 'online' | 'offline' | 'reconnecting'

/**
 * Runtime capabilities the engine advertises via `/health` (non-authenticated).
 * Fase 2 — Camada A: `preventivePlanBuilder` reflects the engine flag
 * `FF_PREVENTIVE_PLAN_BUILDER`. Absent (`undefined`) ⇒ not yet probed / engine
 * velho without the field ⇒ FAIL-OPEN (see `canBuildPreventivePlan`).
 */
export interface EngineCapabilities {
  preventivePlanBuilder?: boolean
}

interface EngineState {
  status: EngineStatus
  reconnectAttempts: number
  lastHealthCheck: number | null
  /** Capability handshake (SPEC v5.0 §2 PR-B.4, A2). Written by
   *  `ConnectionManager.probeCapabilities()` (the LIVE connect() path) and read by
   *  `useEngineCapabilities()`. Default `{}` = capability not yet read (fail-open). */
  engineCapabilities: EngineCapabilities
  setStatus: (status: EngineStatus) => void
  incrementReconnectAttempts: () => void
  resetReconnectAttempts: () => void
  setLastHealthCheck: (timestamp: number) => void
  setEngineCapabilities: (caps: EngineCapabilities) => void
}

export const useEngineStore = create<EngineState>((set) => ({
  status: 'offline',
  reconnectAttempts: 0,
  lastHealthCheck: null,
  engineCapabilities: {},
  setStatus: (status) => set({ status }),
  incrementReconnectAttempts: () =>
    set((state) => ({ reconnectAttempts: state.reconnectAttempts + 1 })),
  resetReconnectAttempts: () => set({ reconnectAttempts: 0 }),
  setLastHealthCheck: (timestamp) => set({ lastHealthCheck: timestamp }),
  setEngineCapabilities: (caps) => set({ engineCapabilities: caps }),
}))

/**
 * Selector hook for the engine capability snapshot. Components gate behind
 * `canBuildPreventivePlan(engineCaps)` — see `preventive-plan-builder-flag.ts`.
 */
export function useEngineCapabilities(): EngineCapabilities {
  return useEngineStore((s) => s.engineCapabilities)
}
